import React, { useEffect } from "react";
import "./App.scss";
import { Route, Routes, Navigate } from "react-router-dom";
import AuthPage from "./pages/AuthPage/AuthPage";
import OrderProcessingPage from "./pages/OrderProcessingPage/OrderProcessingPage";
import PharmacistSupportPage from "./pages/PharmacistSupportPage/PharmacistSupportPage";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "./store";
import { fetchMarketplaceInfo, loginCheckState } from "./store/auth-actions";
import ArticlesOutletPage from "./pages/ArticlesOutletPage/ArticlesOutletPage";
import CommercialsPage from "./pages/CommercialsPage/CommercialsPage";
import Layout from "./components/Layout/Layout";
import ArticlesVisibilityPage from "./pages/ArticlesVisibilityPage/ArticlesVisibilityPage";
import RFZOReminderPage from "./pages/RFZOReminderPage/RFZOReminderPage";
import OurPatientsPage from "./pages/OurPatientsPage/OurPatientsPage";
import PushNotificationsPage from "./pages/PushNotificationsPage/PushNotificationsPage";
import Statistics from "./pages/Statistics/Statistics";
import RFZOPatientsReminderPage from "./pages/RFZOPatientsReminderPage/RFZOPatientsReminderPage";
import GPSChecker from "./components/GPSChecker/GPSChecker";
import VolonterProfile from "./components/VolonterProfile/VolonterProfile";
import VolunteerProfilePage from "./pages/VolunteerProfilePage/VolunteerProfilePage";
import MyCustomersPage from "./pages/MyCustomersPage/MyCustomersPage";
import LoyaltyTransactions from "./pages/LoyaltyTransactions/LoyaltyTransactions";
import DeliveryHistoryPage from "./pages/DeliveryHistoryPage/DeliveryHistoryPage";
import { LocationProvider } from "./contexts/LocationContext";
import {
  selectIsAdvisorSession,
  selectIsAssociationSession,
} from "./store/auth-slice";
import { resolveSessionPersona } from "./utils/sessionPersona";
import { useDefaultRoute } from "./hooks/useDefaultRoute";
import AdvisorContextLoader from "./components/AdvisorContextLoader";
import AccountPanel from "./components/AccountPanel/AccountPanel";
import AdvisorLogoutButton from "./components/AdvisorLogoutButton/AdvisorLogoutButton";
import AssociationLogoutButton from "./components/AssociationLogoutButton/AssociationLogoutButton";
import AssociationContextLoader from "./components/AssociationContextLoader";
import AssociationProfilePage from "./pages/AssociationProfilePage/AssociationProfilePage";
import AssociationAdvisorsPage from "./pages/AssociationAdvisorsPage/AssociationAdvisorsPage";
import AdvisorProfilePage from "./pages/AdvisorProfilePage/AdvisorProfilePage";
import VideoConsultManager from "./components/VideoConsultManager/VideoConsultManager";
import ConsultOrderManager from "./components/ConsultOrder/ConsultOrderManager";
import TabNotificationManager from "./components/TabNotificationManager";
import { selectPersonaKey, selectTokenUserId } from "./store/auth-slice";

// Loading component for authentication initialization
const AuthLoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      fontSize: "18px",
      fontFamily: "Arial, sans-serif",
      color: "#333",
    }}
  >
    Inicijalizacija autentifikacije...
  </div>
);

function App() {
  const dispatch = useDispatch();

  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.token !== null,
  );
  const authInitialized = useSelector(
    (state: RootState) => state.auth.authInitialized,
  );
  const isFavouriteVolunteer = useSelector(
    (state: RootState) =>
      state.auth.userInfo?.roles?.includes("FAVOURITE_DELIVERY_USER") ?? false,
  );
  // ITAM-2887: the advisor persona gets its own route subtree, no GPS checker
  // and no retail marketplace fetch.
  const isAdvisor = useSelector(selectIsAdvisorSession);
  // Association portal: its own route subtree too; shares the advisor's
  // "no GPS, no marketplace" rules but mounts no consult stack.
  const isAssociation = useSelector(selectIsAssociationSession);
  const isPortalPersona = isAdvisor || isAssociation;
  const tokenUserId = useSelector(selectTokenUserId);
  const personaKey = useSelector(selectPersonaKey);
  const defaultRoute = useDefaultRoute();

  useEffect(() => {
    dispatch<any>(loginCheckState());
    // Only fetch marketplace info for the staff personas; a driver has no
    // marketplace and GET /marketplace/user/{id} is retail-only, so an advisor
    // must skip it too.
    const roles = localStorage.getItem("roles")?.split(",") || [];
    const persona = resolveSessionPersona(roles);
    if (
      persona !== "DRIVER" &&
      persona !== "ADVISOR" &&
      persona !== "ASSOCIATION" &&
      persona !== "UNSUPPORTED"
    ) {
      dispatch<any>(
        fetchMarketplaceInfo(parseInt(localStorage.getItem("id")!)),
      );
    }
  }, [dispatch]);

  // Show loading spinner during authentication initialization
  if (!authInitialized) {
    return <AuthLoadingSpinner />;
  }

  // Shared guard for the favourite-volunteer routes (ITAM-2677 / ITAM-2734)
  const favouriteVolunteerRoute = (element: React.ReactElement) =>
    isFavouriteVolunteer ? (
      element
    ) : (
      <Navigate to={defaultRoute} replace />
    );

  return (
    <LocationProvider>
      <Layout>
        {!isPortalPersona && <GPSChecker />}
        {isAuthenticated && isAssociation && <AssociationContextLoader />}
        {isAuthenticated && isAdvisor && (
          <>
            <AdvisorContextLoader />
            <VideoConsultManager />
            {/* Keyed on the session identity so a persona-changing token
                refresh tears the consult-order state down with it. */}
            <ConsultOrderManager key={`${tokenUserId}:${personaKey}`} />
            <TabNotificationManager />
          </>
        )}
        <Routes>
          {!isAuthenticated ? (
            // ==========================================
            // UNAUTHENTICATED ROUTES
            // ==========================================
            <>
              <Route path="/auth/*" element={<AuthPage />} />
              {/* Redirect all other routes to auth */}
              <Route path="*" element={<Navigate to="/auth" replace />} />
            </>
          ) : isAdvisor ? (
            // ==========================================
            // ADVISOR ROUTES (ITAM-2887): profile and logout only
            // ==========================================
            <>
              <Route
                path="/advisor-profile"
                element={<AdvisorProfilePage />}
              />
              <Route
                path="/profile"
                element={
                  <AccountPanel logoutControl={<AdvisorLogoutButton />} />
                }
              />
              <Route
                path="*"
                element={<Navigate to="/advisor-profile" replace />}
              />
            </>
          ) : isAssociation ? (
            // ==========================================
            // ASSOCIATION ROUTES: the administered association and logout
            // ==========================================
            <>
              <Route path="/association" element={<AssociationProfilePage />} />
              <Route
                path="/association/advisors"
                element={<AssociationAdvisorsPage />}
              />
              <Route
                path="/profile"
                element={
                  <AccountPanel logoutControl={<AssociationLogoutButton />} />
                }
              />
              <Route
                path="*"
                element={<Navigate to="/association" replace />}
              />
            </>
          ) : (
            // ==========================================
            // AUTHENTICATED ROUTES
            // ==========================================
            <>
              {/* Redirect auth pages to appropriate dashboard */}
              <Route
                path="/auth/*"
                element={<Navigate to={defaultRoute} replace />}
              />

              {/* Main application routes */}
              <Route
                path="/order-processing"
                element={<OrderProcessingPage />}
              />
              <Route path="/profile" element={<VolonterProfile />} />
              <Route
                path="/authorized-person-profile"
                element={favouriteVolunteerRoute(<VolunteerProfilePage />)}
              />
              <Route
                path="/my-customers"
                element={favouriteVolunteerRoute(<MyCustomersPage />)}
              />
              <Route
                path="/my-activity"
                element={<LoyaltyTransactions />}
              />
              <Route
                path="/completed-reservations"
                element={<DeliveryHistoryPage />}
              />

              <Route
                path="/pharmacist-support"
                element={<PharmacistSupportPage />}
              />
              <Route path="/articles-outlet" element={<ArticlesOutletPage />} />
              <Route
                path="/articles-visibility"
                element={<ArticlesVisibilityPage />}
              />
              <Route
                path="/web-commercials"
                element={<CommercialsPage web />}
              />
              <Route
                path="/mobile-commercials"
                element={<CommercialsPage mobile />}
              />
              <Route
                path="/rfzo-patient-reminder"
                element={<RFZOPatientsReminderPage />}
              />
              <Route
                path="/rfzo-article-reminder"
                element={<RFZOReminderPage rfzoArticles />}
              />
              <Route path="/patients" element={<OurPatientsPage />} />
              <Route path="/statistics" element={<Statistics />} />
              <Route
                path="/push-notifications"
                element={<PushNotificationsPage />}
              />

              {/* Default route redirect */}
              <Route
                path="/"
                element={<Navigate to={defaultRoute} replace />}
              />

              {/* Catch-all for authenticated users */}
              <Route
                path="*"
                element={<Navigate to={defaultRoute} replace />}
              />
            </>
          )}
        </Routes>
      </Layout>
    </LocationProvider>
  );
}

export default App;
